import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { supabase } from '../services/supabaseClient';
import { ROLE_META } from '../utils/constants';

/**
 * Training KPI Dashboard - Admin Only
 * Shows training progress for all users
 */
export default function TrainingKPIPage() {
  const { currentUser } = useContext(AppContext);
  const [users, setUsers] = useState([]);
  const [trainingDocs, setTrainingDocs] = useState([]);
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [showMeetingRequestsOnly, setShowMeetingRequestsOnly] = useState(false);

  useEffect(() => {
    loadTrainingKPI();
  }, []);

  const loadTrainingKPI = async () => {
    setLoading(true);
    try {
      // Load all users (exclude admin)
      const { data: usersData } = await supabase
        .from('users')
        .select('*')
        .neq('role', 'admin')
        .order('created_at', { ascending: false });

      // Load training documents
      const { data: docsData } = await supabase
        .from('shared_documents')
        .select('*')
        .eq('category', 'training')
        .order('order_number', { ascending: true });

      // Load all quiz attempts
      const { data: attemptsData } = await supabase
        .from('user_quiz_attempts')
        .select(`
          *,
          users!inner(full_name, role)
        `)
        .order('attempted_at', { ascending: false });

      // Load learning progress for all users
      const { data: progressData } = await supabase
        .from('user_learning_progress')
        .select('*');

      setUsers(usersData || []);
      setTrainingDocs(docsData || []);
      setQuizAttempts(attemptsData || []);
      
      // Store progress data for later use
      window._trainingProgress = progressData || [];
    } catch (err) {
      console.error('Load training KPI error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    if (showMeetingRequestsOnly && !user.call_meeting_requested) return false;
    if (filterRole !== 'all' && user.role !== filterRole) return false;
    if (searchTerm && !user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Get user's document read status
  const getUserDocProgress = (userId) => {
    const progress = window._trainingProgress || [];
    const userProgress = progress.filter(p => p.user_id === userId);
    const readDocs = userProgress.filter(p => p.is_completed).map(p => p.doc_id);
    return {
      total: trainingDocs.length,
      read: readDocs.length,
      readDocIds: readDocs,
      percentage: trainingDocs.length > 0 ? Math.round((readDocs.length / trainingDocs.length) * 100) : 0,
    };
  };

  // Get user's quiz info
  const getUserQuizInfo = (userId) => {
    const userAttempts = quizAttempts.filter(a => a.user_id === userId);
    const latestAttempt = userAttempts[0]; // Most recent (ordered by attempted_at DESC)
    return {
      totalAttempts: userAttempts.length,
      latestScore: latestAttempt?.score_percentage || null,
      passed: latestAttempt?.passed || false,
      latestAttemptAt: latestAttempt?.attempted_at || null,
    };
  };

  // Calculate overall stats
  const totalUsers = users.length;
  const avgProgress = totalUsers > 0 
    ? Math.round(users.reduce((sum, u) => sum + (u.learning_progress || 0), 0) / totalUsers)
    : 0;
  const quizPassedCount = users.filter(u => u.quiz_passed).length;
  const readyForCallCount = users.filter(u => u.is_ready_for_call).length;
  const meetingRequestCount = users.filter(u => u.call_meeting_requested).length;

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: '#000000', fontSize: 28, fontWeight: 900, marginBottom: 8 }}>
          📊 لوحة تدريب الفريق
        </h1>
        <p style={{ color: '#000000', fontSize: 14 }}>
          متابعة تقدم الفريق في التدريب والاختبارات
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>إجمالي المتدربين</div>
          <div style={{ color: '#0f172a', fontSize: 32, fontWeight: 900 }}>{totalUsers}</div>
        </div>
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>متوسط التقدم</div>
          <div style={{ color: '#4ecdc4', fontSize: 32, fontWeight: 900 }}>{avgProgress}%</div>
        </div>
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>اجتازوا الاختبار</div>
          <div style={{ color: '#10b981', fontSize: 32, fontWeight: 900 }}>{quizPassedCount}</div>
        </div>
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>جاهزون للمكالمات</div>
          <div style={{ color: '#f59e0b', fontSize: 32, fontWeight: 900 }}>{readyForCallCount}</div>
        </div>
        <div 
          onClick={() => setShowMeetingRequestsOnly(!showMeetingRequestsOnly)}
          style={{ 
            background: showMeetingRequestsOnly ? '#d1fae5' : '#ffffff', 
            border: `2px solid ${showMeetingRequestsOnly ? '#10b981' : '#e2e8f0'}`, 
            borderRadius: 12, 
            padding: 20,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}
        >
          <div style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>طلبات اجتماع المكالمة</div>
          <div style={{ color: '#10b981', fontSize: 32, fontWeight: 900 }}>{meetingRequestCount}</div>
          {showMeetingRequestsOnly && (
            <div style={{ color: '#10b981', fontSize: 11, marginTop: 4, fontWeight: 700 }}>
              عرض الطلبات فقط ✓
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <input
          type="text"
          placeholder="🔍 بحث بالاسم..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            padding: '10px 16px',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            color: '#0f172a',
            fontSize: 14,
            fontFamily: "'Cairo', sans-serif",
          }}
        />
        <select
          value={filterRole}
          onChange={e => setFilterRole(e.target.value)}
          style={{
            padding: '10px 16px',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            color: '#0f172a',
            fontSize: 14,
            fontFamily: "'Cairo', sans-serif",
          }}
        >
          <option value="all">كل الأدوار</option>
          {Object.entries(ROLE_META)
            .filter(([key]) => key !== 'admin')
            .map(([key, meta]) => (
              <option key={key} value={key}>{meta.label}</option>
            ))}
        </select>
      </div>

      {/* Users Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#000000' }}>جاري التحميل...</div>
      ) : (
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>المتدرب</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>الدور</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>التسجيلات المجانية</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>التقدم</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>الاختبار</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>الحالة</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>إجراء</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => {
                const docProgress = getUserDocProgress(user.id);
                const quizInfo = getUserQuizInfo(user.id);
                const rm = ROLE_META[user.role] || {};

                return (
                  <>
                    <tr 
                      key={user.id}
                      style={{ 
                        borderBottom: '1px solid #e2e8f0',
                        cursor: 'pointer',
                        background: expandedUser === user.id ? '#f1f5f9' : 'transparent',
                      }}
                      onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
                    >
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ color: '#000000', fontWeight: 700, fontSize: 14 }}>{user.full_name}</div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ 
                          background: (rm.color || '#64748b') + '22', 
                          color: rm.color || '#64748b', 
                          padding: '4px 10px', 
                          borderRadius: 20, 
                          fontSize: 11, 
                          fontWeight: 700 
                        }}>
                          {rm.label || user.role}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {(user.role === 'marketer' || user.role === 'rep') ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ 
                              background: user.free_registrations_remaining > 0 ? '#10b98122' : '#ef444422', 
                              color: user.free_registrations_remaining > 0 ? '#10b981' : '#ef4444', 
                              padding: '4px 10px', 
                              borderRadius: 20, 
                              fontSize: 13, 
                              fontWeight: 900 
                            }}>
                              {user.free_registrations_remaining ?? 0}/5
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: '#64748b', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ 
                              width: `${docProgress.percentage}%`, 
                              height: '100%', 
                              background: docProgress.percentage === 100 ? '#10b981' : '#3b82f6',
                              borderRadius: 4,
                              transition: 'width 0.3s ease',
                            }} />
                          </div>
                          <span style={{ color: '#000000', fontSize: 13, fontWeight: 700, minWidth: 40 }}>
                            {docProgress.percentage}%
                          </span>
                        </div>
                        <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>
                          {docProgress.read}/{docProgress.total} مستند
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {quizInfo.totalAttempts > 0 ? (
                          <div>
                            <div style={{ 
                              color: quizInfo.passed ? '#10b981' : '#ef4444', 
                              fontSize: 14, 
                              fontWeight: 700 
                            }}>
                              {quizInfo.latestScore}%
                            </div>
                            <div style={{ color: '#64748b', fontSize: 11 }}>
                              {quizInfo.totalAttempts} محاولة
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: '#64748b', fontSize: 12 }}>لم يحاول</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {user.is_ready_for_call ? (
                          <span style={{ background: '#10b98122', color: '#10b981', padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                            ✅ جاهز
                          </span>
                        ) : (
                          <span style={{ background: '#f59e0b22', color: '#f59e0b', padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                            ⏳ قيد التدريب
                          </span>
                        )}
                        {user.call_meeting_requested && (
                          <div style={{ marginTop: 6 }}>
                            <span style={{ background: '#3b82f622', color: '#3b82f6', padding: '4px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>
                              📞 طلب اجتماع
                            </span>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedUser(expandedUser === user.id ? null : user.id);
                          }}
                          style={{
                            background: 'transparent',
                            border: '1px solid #334155',
                            color: '#000000',
                            padding: '4px 12px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontFamily: "'Cairo', sans-serif",
                          }}
                        >
                          {expandedUser === user.id ? 'إخفاء' : 'تفاصيل'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Details */}
                    {expandedUser === user.id && (
                      <tr key={`${user.id}-detail`}>
                        <td colSpan="6" style={{ padding: 20, background: '#ffffff' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                            {/* Document Progress Details */}
                            <div>
                              <h4 style={{ color: '#000000', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
                                📖 المستندات المقروءة ({docProgress.read}/{docProgress.total})
                              </h4>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {trainingDocs.map(doc => {
                                  const isRead = docProgress.readDocIds.includes(doc.id);
                                  return (
                                    <div key={doc.id} style={{ 
                                      padding: '8px 12px',
                                      background: isRead ? '#10b98111' : '#f1f5f9',
                                      border: `1px solid ${isRead ? '#10b98144' : '#e2e8f0'}`,
                                      borderRadius: 8,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 8,
                                    }}>
                                      <span style={{ fontSize: 16 }}>{isRead ? '✅' : '❌'}</span>
                                      <span style={{ 
                                        color: isRead ? '#10b981' : '#94a3b8', 
                                        fontSize: 13,
                                        fontWeight: isRead ? 700 : 500,
                                      }}>
                                        {doc.title}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Quiz History */}
                            <div>
                              <h4 style={{ color: '#000000', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
                                📝 سجل الاختبارات ({quizInfo.totalAttempts} محاولة)
                              </h4>
                              {quizInfo.totalAttempts > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                  {quizAttempts
                                    .filter(a => a.user_id === user.id)
                                    .slice(0, 5)
                                    .map((attempt, idx) => (
                                      <div key={attempt.id} style={{ 
                                        padding: '10px 12px',
                                        background: attempt.passed ? '#10b98111' : '#ef444411',
                                        border: `1px solid ${attempt.passed ? '#10b98144' : '#ef444444'}`,
                                        borderRadius: 8,
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                          <span style={{ 
                                            color: attempt.passed ? '#10b981' : '#ef4444', 
                                            fontSize: 16, 
                                            fontWeight: 900 
                                          }}>
                                            {attempt.score_percentage}%
                                          </span>
                                          <span style={{ 
                                            color: '#64748b', 
                                            fontSize: 11 
                                          }}>
                                            {new Date(attempt.attempted_at).toLocaleDateString('ar-EG')}
                                          </span>
                                        </div>
                                        <div style={{ color: '#000000', fontSize: 12 }}>
                                          {attempt.correct_answers}/{attempt.total_questions} إجابة صحيحة
                                          {attempt.passed && ' ✅ اجتاز'}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              ) : (
                                <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: 20 }}>
                                  لم يقم بأي اختبار بعد
                                </div>
                              )}

                              {/* Meeting Request Info */}
                              {user.call_meeting_requested && (
                                <div style={{ 
                                  marginTop: 16,
                                  padding: '12px 16px',
                                  background: '#3b82f611',
                                  border: '1px solid #3b82f644',
                                  borderRadius: 8,
                                }}>
                                  <div style={{ color: '#3b82f6', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                                    📞 طلب اجتماع المكالمة
                                  </div>
                                  <div style={{ color: '#000000', fontSize: 12 }}>
                                    تم الطلب: {user.call_meeting_requested_at 
                                      ? new Date(user.call_meeting_requested_at).toLocaleDateString('ar-EG', {
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })
                                      : 'غير معروف'}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              لا يوجد مستخدمين مطابقين للفلتر
            </div>
          )}
        </div>
      )}
    </div>
  );
}
